import { initializeStrapiBackend } from '../Contentful_API/strapiBackend';

/**
 * Public consultation endpoints (Strapi Post + Comment collections).
 * The CMS only returns enabled, published posts and moderated comments.
 */

async function request(path, options = {}) {
    const { apiUrl, uploadsUrl } = await initializeStrapiBackend();
    const response = await fetch(`${apiUrl}${path}`, { credentials: 'omit', ...options });
    return { response, uploadsUrl };
}

function toPost(item, uploadsUrl) {
    const attributes = item?.attributes || {};
    const documents = (attributes.documents?.data || [])
        .map((file) => file?.attributes)
        .filter((file) => file && file.url)
        .map((file) => ({
            name: file.name,
            url: file.url.startsWith('http') ? file.url : `${uploadsUrl}${file.url}`,
            size: file.size,
        }));
    return {
        id: item.id,
        title: attributes.title || '',
        slug: attributes.slug || '',
        description: attributes.description || '',
        category: attributes.category?.data?.attributes?.name || '',
        publishedAt: attributes.publishedAt || '',
        // Optional consultation window; the CMS only returns posts inside it.
        startDate: attributes.startDate || '',
        endDate: attributes.endDate || '',
        cover: toCover(attributes.coverImage?.data?.attributes, uploadsUrl),
        documents,
    };
}

/**
 * Cover picture in two sizes: `card` for the listing (medium, ~750px) and `large` for the page
 * header. Strapi only makes the smaller formats for big uploads, so each falls back to the original.
 */
function toCover(file, uploadsUrl) {
    if (!file?.url) return null;
    const absolute = (url) => (url.startsWith('http') ? url : `${uploadsUrl}${url}`);
    const formats = file.formats || {};
    return {
        card: absolute((formats.medium || formats.large || file).url),
        large: absolute((formats.large || file).url),
        alt: file.alternativeText || '',
    };
}

/** e.g. "30 November 2026, 17:00 (Dili time)"; '' when there is no date. */
export function formatDeadline(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${date.toLocaleString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dili',
    })} (Dili time)`;
}

export async function listConsultations() {
    const { response, uploadsUrl } = await request(
        '/posts?sort=publishedAt:desc&pagination[pageSize]=100'
    );
    if (!response.ok) throw new Error('Consultations could not be loaded.');
    const result = await response.json();
    return (result.data || []).map((item) => toPost(item, uploadsUrl));
}

export async function getConsultation(slug) {
    const { response, uploadsUrl } = await request(
        `/posts?filters[slug][$eq]=${encodeURIComponent(slug)}&pagination[pageSize]=1`
    );
    if (!response.ok) throw new Error('The consultation could not be loaded.');
    const result = await response.json();
    return result.data?.[0] ? toPost(result.data[0], uploadsUrl) : null;
}

// Strapi caps a page at 100 entries; stop after this many pages so a runaway thread can't stall the page.
const MAX_COMMENT_PAGES = 20;

/** All published comments and replies on a post, oldest first. Replies carry their thread's parentId. */
export async function listComments(postId) {
    const comments = [];
    for (let page = 1; page <= MAX_COMMENT_PAGES; page += 1) {
        const { response } = await request(
            `/comments?filters[post][id][$eq]=${encodeURIComponent(postId)}&sort=publishedAt:asc` +
            `&pagination[page]=${page}&pagination[pageSize]=100`
        );
        if (!response.ok) throw new Error('Comments could not be loaded.');
        const result = await response.json();
        (result.data || []).forEach((item) => comments.push({
            id: item.id,
            parentId: item.attributes?.parent?.data?.id || null,
            authorName: item.attributes?.authorName || '',
            content: item.attributes?.content || '',
            publishedAt: item.attributes?.publishedAt || '',
            isMinistryReply: Boolean(item.attributes?.isMinistryReply),
        }));
        if (page >= (result.meta?.pagination?.pageCount || 1)) break;
    }
    return comments;
}

/**
 * Returns 'ok', 'rate-limited', 'closed', 'captcha', 'unavailable' or 'error'.
 * Pass parentId to reply to a comment. captchaToken is the Turnstile token; website is the honeypot.
 */
export async function submitComment({ postId, parentId, authorName, content, captchaToken, website }) {
    const data = { post: postId, authorName, content };
    if (parentId) data.parent = parentId;
    if (captchaToken) data.captchaToken = captchaToken;
    if (website) data.website = website;
    const { response } = await request('/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data }),
    });
    if (response.ok) return 'ok';
    if (response.status === 429) return 'rate-limited';
    if (response.status === 400) return 'closed';
    if (response.status === 403) return 'captcha';
    if (response.status === 503) return 'unavailable';
    return 'error';
}
